import { Add, Delete, Download, Edit, FilterAlt } from "@mui/icons-material";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  ButtonContainer,
  StyledChoiceFileText,
  StyledContainerBox,
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
  StyledUploadBox,
} from "./DisplayClassFiles.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import useHttp from "hooks/use-http";
import { PAGINATION_LIMIT } from "constants";
import {
  addTeacherClassFileRequest,
  getEducationYearRequest,
  getTeacherClassFileRequest,
} from "api/requests";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { FILE_BASE_URL } from "constants";
import useDownloader from "react-use-downloader";
import { toast } from "react-toastify";
import LoadingCM from "components/shared/Loading";
import { IoMdCloudUpload } from "react-icons/io";
import { yupResolver } from "@hookform/resolvers/yup";
import { teacherUploadFileSchema, validateFile } from "validations";
import Toast from "helper/toast";
import ProgressBarCM from "components/shared/ProgressBar";
import { useCallback } from "react";
import BackButton from "components/shared/BackButton";

const headCells = generateHeadCells("teacher_class_file");

export default function TeacherClassFiles() {
  const { class_id } = useParams();
  const navigate = useNavigate();

  const [newFile, setNewFile] = useState(null);

  const { download, error, cancel } = useDownloader();

  // http hook
  const { loading, request, uploadProgress, isUploading, setUploadProgress } =
    useHttp();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(teacherUploadFileSchema) });

  // filter option state
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  const [classFiles, setClassFiles] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });

  // class name state
  const [className, setClassName] = useState("");

  // paginition state
  const [page, setPage] = useState(1);

  //handle download
  const handleDownload = (id, fileUrl) => {
    const fileName = fileUrl.split("/");
    download(FILE_BASE_URL + fileUrl, fileName[fileName.length - 1]);
  };

  const [educationalYears, setEducationalYears] = useState([]);
  const getEducationYear = useCallback(async () => {
    const res = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });
    if (res) {
      setEducationalYears(res);
      setSelectedEducationalYear({ value: res[res.length - 2] });
    }
  }, []);

  // add new file
  const onSubmit = async (data) => {
    if (!newFile) {
      return Toast({
        type: "custom-warn",
        title: `فایل باید انتخاب شود`,
        autoClose: 3000,
      });
    }
    const fileTypeValidate = data.file.length
      ? validateFile(data.file[0], "فایل", 15_000_000, [
          "png",
          "jpg",
          "jpeg",
          "pdf",
          "zip",
          "rar",
        ])
      : true;

    if (!fileTypeValidate) {
      return null;
    }

    const res = await request({
      url: () =>
        addTeacherClassFileRequest(
          {
            class_id: `${class_id}`,
            title: data.title ?? null,
            file: data.file[0] ?? null,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });

    if (res) {
      Toast({
        type: "success",
        title: `افزودن فایل`,
        autoClose: 3000,
      });
      reset({
        file: "",
        title: "",
      });

      setNewFile(null);
      getClassFiles(false);
    }
  };

  // get class files
  const getClassFiles = async (filter = true) => {
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      educational_year: selectedEducationalYear?.value ?? "*",
      class_id,
    };

    const res = await request({
      url: getTeacherClassFileRequest,
      values: filter
        ? query
        : {
            skip: 0,
            limit: PAGINATION_LIMIT,
            educational_year: "*",
            class_id,
          },
    });

    if (!res.count || !res.data.length) {
      return setClassFiles({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    }

    const transformedData = res.data.map((item) => ({
      id: 3,
      title: item.title ?? "ثبت نشده",
      sender_type: item.added_by_type === "teacher" ? "مدرس" : "ادمین",
      educational_year: item.educational_year ?? "ثبت نشده",
      date: item.created_at.string ?? "ثبت نشده",
      file: (
        <IconButton
          color="primary"
          onClick={() => handleDownload(item.id, item.file)}
        >
          <Tooltip title="دانلود فایل">
            <Download />
          </Tooltip>
        </IconButton>
      ),
      edit: (
        <IconButton
          color="primary"
          onClick={() => navigate(`/teacher_class/file/${item.id}`)}
        >
          <Tooltip title="ویرایش">
            <Edit />
          </Tooltip>
        </IconButton>
      ),
    }));

    setClassFiles({
      data: transformedData,
      count: res.count,
    });
    setClassName(res.data[0].class.title);
  };

  const getWithFilter = () => {
    setPage(1);
    getClassFiles();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setSelectedEducationalYear(null);
    setSelectedEducationalYear("");
    getClassFiles(false);
  };

  useEffect(() => {
    getClassFiles();
    getEducationYear();
  }, [page, getEducationYear]);

  useEffect(() => {
    if (error) {
      cancel();
      toast.error("مشکلی در دانلود پیش آمد", { position: "top-center" });
    }
  }, [error]);
  return (
    <DrawerCM title={" فایل های کلاس " + className} $styledHeader>
      <Grid item xs={12} my={3}>
        <BackButton />
      </Grid>
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <>
          <StyledStack>
            <Grid container>
              <Grid item sm={6} xs={12} p={1}>
                <TextField
                  label="عنوان"
                  variant="outlined"
                  fullWidth
                  id="title"
                  name="title"
                  type={"text"}
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors?.title?.message}
                />
              </Grid>
              <Grid item sm={6} xs={12} p={1}>
                <StyledContainerBox>
                  <StyledUploadBox>
                    <StyledChoiceFileText>
                      {newFile ? newFile : "آپلود فایل جدید"}
                    </StyledChoiceFileText>
                    <IoMdCloudUpload />
                  </StyledUploadBox>
                  <TextField
                    variant="outlined"
                    type="file"
                    fullWidth
                    id="file"
                    name="file"
                    {...register("file")}
                    onChange={(e) => setNewFile(e.target.files[0].name)}
                  />
                </StyledContainerBox>
              </Grid>
              <Grid item sm={12} xs={12}>
                <ButtonContainer>
                  <Button
                    color="success"
                    variant="contained"
                    size="large"
                    endIcon={<Add />}
                    onClick={handleSubmit(onSubmit)}
                  >
                    افزودن
                  </Button>
                </ButtonContainer>
              </Grid>
            </Grid>
          </StyledStack>
          <StyledStack>
            <Grid container>
              <Grid item sm={6} xs={12} p={0.5}>
                <Controller
                  control={control}
                  name="select-educational-year"
                  defaultValue={""}
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears?.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={4} p={0.5}></Grid>
              <StyledGridIcons item sm={2} xs={12} p={1}>
                <IconButton color="primary" onClick={getWithFilter}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton color="error" onClick={getWithoutFilter}>
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                rows={classFiles.data}
                page={page}
                headCells={headCells}
                colspan={classFiles.count === 0 ? "10" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
              {classFiles.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={classFiles.count}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </DrawerCM>
  );
}
